import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useProjectStore } from '@/store/projectStore';
import { projectService } from '@/services/projectService';

interface UserSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  projectId?: number;
}

export function UserSidebar({ isOpen = false, onClose, projectId }: UserSidebarProps) {
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const setSelectedProjectId = useProjectStore((s) => s.setSelectedProjectId);

  const { data: myRole } = useQuery({
    queryKey: ['project-my-role', projectId],
    queryFn: () => projectService.getMyProjectRole(projectId as number),
    enabled: typeof projectId === 'number' && Number.isFinite(projectId),
    retry: 1,
  });

  const currentUserRole = useMemo(() => {
    return myRole?.role ?? null;
  }, [myRole?.role]);

  const roleLabel = useMemo(() => {
    if (!currentUserRole) return 'Utilisateur';
    switch (currentUserRole) {
      case 'PROJECT_ADMIN':
        return 'Administrateur';
      case 'PROJECT_MANAGER':
        return 'Manager';
      case 'PROJECT_USER':
        return 'Utilisateur';
      default:
        return 'Utilisateur';
    }
  }, [currentUserRole]);

  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [projectId, setSelectedProjectId]);

  const normalizePath = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

  const basePath = projectId ? `/dashboard/${projectId}` : '/dashboard';

  const isActive = (path: string) => {
    const current = normalizePath(location.pathname);
    const target = normalizePath(path);

    if (target === normalizePath(basePath)) {
      return current === target;
    }

    return current === target || current.startsWith(target + '/');
  };

  const getUserInitials = (fullName: string) => {
    const parts = fullName.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.slice(0, 2) || 'US').toUpperCase();
  };

  useEffect(() => {
    if (isOpen && onClose) {
      onClose();
    }
  }, [location.pathname, isOpen, onClose]);

  const menuItems = [
    {
      path: basePath,
      label: "Vue d'ensemble",
      icon: 'dashboard',
    },
    {
      path: `${basePath}/carte`,
      label: 'Carte',
      icon: 'map',
      hoverColor: 'group-hover:text-blue-400',
    },
    {
      path: `${basePath}/boxs`,
      label: 'Équipement',
      icon: 'devices',
      hoverColor: 'group-hover:text-teal-400',
    },
    {
      path: `${basePath}/logements`,
      label: 'Logements',
      icon: 'apartment',
      hoverColor: 'group-hover:text-purple-400',
    },
    {
      path: `${basePath}/planning`,
      label: 'Planning',
      icon: 'calendar_today',
      hoverColor: 'group-hover:text-blue-400',
    },
    {
      path: `${basePath}/parties-communes`,
      label: 'Parties communes',
      icon: 'meeting_room',
      hoverColor: 'group-hover:text-green-400',
    },
    {
      path: `${basePath}/defauts`,
      label: 'Défauts',
      icon: 'warning',
      hoverColor: 'group-hover:text-red-400',
    },
    {
      path: `${basePath}/documentations`,
      label: 'Documentations',
      icon: 'description',
      hoverColor: 'group-hover:text-blue-400',
    },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-white dark:bg-slate-800
          border-r border-slate-200 dark:border-slate-700
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col flex-shrink-0
        `}
      >
        {/* Header */}
        <div className="h-16 flex items-center px-6 border-b border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-lg bg-primary/20 border border-primary/30">
              <span className="material-icons-outlined text-primary" style={{ fontSize: '20px' }}>apartment</span>
            </div>
            <span className="text-base font-bold text-slate-900 dark:text-white tracking-tight">SMALT GTB</span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden ml-auto p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
            >
              <span className="material-icons-outlined">close</span>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1.5">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all
                ${isActive(item.path)
                  ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600 border border-transparent'
                }
                ${item.badge ? 'justify-between' : ''}
              `}
            >
              <span className={`material-icons-outlined text-xl mr-3 ${item.hoverColor || ''} transition-colors`} style={{ fontSize: '20px' }}>
                {item.icon}
              </span>
              {item.label}
              {item.badge && item.badge > 0 && (
                <span className="ml-auto bg-red-500/90 text-white py-0.5 px-2 rounded-md text-xs font-bold shadow-sm">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* Footer: user + logout */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/30">
          <Link
            to="/dashboard/profile"
            className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
          >
            <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
              {user?.fullName ? getUserInitials(user.fullName) : 'US'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.fullName || 'Utilisateur'}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || ''}</p>
              {projectId && roleLabel && (
                <p className="text-xs text-primary font-semibold mt-0.5 truncate">{roleLabel}</p>
              )}
            </div>
          </Link>
          <button
            onClick={logout}
            className="mt-2 w-full px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 rounded-md transition-colors flex items-center gap-2"
          >
            <span className="material-icons-outlined text-lg">logout</span>
            Déconnexion
          </button>
        </div>
      </aside>
    </>
  );
}
